import { motion } from 'motion/react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useCreateCohortEditStepMutation,
  useCreateCohortNextStepMutation,
  useCreateCohortRunJobMutation,
} from 'src/api/create-cohort/mutation';
import { useUpdateCohortMutation } from 'src/api/cohorts-queries';
import { PulsingText } from 'src/components/common/pulsing-text';
import { useModal } from 'src/hooks/modal-provider';
import { usedAcceptedStep } from 'src/store/create-cohort';
import { showToast } from 'src/store/toast';
import {
  CodesSelectionStep,
  CohortState,
  CohortStepStatus,
  CohortStepType,
  StepComponentProps,
} from 'src/types/cohorts';
import { to } from 'to-await';
import { SaveCohortModal } from './save-cohort-modal';
import { STEP_CONFIG } from './step-config';
import { StepHeader } from './step-header';
import { STEP_COMPONENTS } from './steps/get-step-component';
import { UserAvatar } from './user-avatar';

type ChatMessagesProps = {
  cohortState: CohortState;
};

export const ChatMessages = ({ cohortState }: ChatMessagesProps) => {
  const { steps, currentStep, rootOrganizationId } = cohortState;

  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();

  const lastHeightRef = useRef(0);
  const stepsContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomElementRef = useRef<HTMLDivElement | null>(null);

  const { mutateAsync: createCohortNextStep } = useCreateCohortNextStepMutation();
  const { mutateAsync: createCohortEditStep } = useCreateCohortEditStepMutation();
  const { mutateAsync: createCohortRunJob, isPending } = useCreateCohortRunJobMutation();
  const { mutateAsync: updateCohort } = useUpdateCohortMutation();

  const scrollToBottom = () => {
    if (!bottomElementRef.current) return;
    bottomElementRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();

    const observer = new MutationObserver(() => {
      if (stepsContainerRef.current) {
        const currentHeight = stepsContainerRef.current.scrollHeight;

        if (currentHeight > lastHeightRef.current) {
          scrollToBottom();
        }

        lastHeightRef.current = currentHeight;
      }
    });

    if (stepsContainerRef.current) {
      lastHeightRef.current = stepsContainerRef.current.scrollHeight;

      observer.observe(stepsContainerRef.current, {
        subtree: true,
        attributes: true,
        childList: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleAcceptClick = async () => {
    if (!cohortState.dataSourceId || !cohortState.cohortId || !currentStep) {
      return;
    }

    dispatch(usedAcceptedStep({ currentStep }));

    const [error] = await to(
      createCohortNextStep({
        dataSourceId: cohortState?.dataSourceId,
        cohortId: cohortState?.cohortId,
        currentStep,
        steps,
        organizationId: rootOrganizationId,
      }),
    );
    if (error) {
      dispatch(showToast({ message: error.message, type: 'dangerous' }));
      return;
    }
  };

  const handleEditClick = async ({
    step,
    editedContent,
  }: {
    step: CohortStepType;
    editedContent: string;
  }) => {
    if (!cohortState.dataSourceId || !cohortState.cohortId) {
      return;
    }
    // TODO: simlify the call to a normal fetch insteaf of using the mutation
    const [error] = await to(
      createCohortEditStep({
        dataSourceId: cohortState.dataSourceId,
        cohortId: cohortState.cohortId,
        step: step,
        editedContent,
      }),
    );

    if (error) {
      dispatch(showToast({ message: error.message, type: 'dangerous' }));
      return;
    }
  };

  const handleCreateCohortClick = async () => {
    if (!cohortState.dataSourceId || !cohortState.cohortId) {
      return;
    }

    // TODO: simlify the call to a normal fetch insteaf of using the mutation
    const [error] = await to(
      createCohortRunJob({
        dataSourceId: cohortState.dataSourceId,
        cohortId: cohortState.cohortId,
        steps: cohortState.steps,
      }),
    );

    if (error) {
      dispatch(showToast({ message: error.message, type: 'dangerous' }));
      return;
    }
    openModal(SaveCohortModal, {
      name: cohortState.name,
      description: cohortState.description,
      onSkip: () => {
        navigate('/account/data/cohorts');
      },
      onClose: closeModal,
      onSave: async ({ name, description }) => {
        try {
          await updateCohort({
            dataSourceId: cohortState.dataSourceId!,
            cohortId: cohortState.cohortId!,
            name,
            description,
          });
          // Navigate after successful update
          navigate('/account/data/cohorts');
        } catch (error) {
          dispatch(showToast({ message: 'Failed to save cohort details', type: 'dangerous' }));
        }
      },
    });
  };

  const getVisibleSteps = (): CohortStepType[] => {
    return Object.values(CohortStepType)
      .filter(
        (stepType) =>
          steps[stepType] &&
          (steps[stepType].data?.length > 0 ||
            steps[stepType].status === CohortStepStatus.In_Progress),
      )
      .sort((a, b) => STEP_CONFIG[a].order - STEP_CONFIG[b].order);
  };

  const visibleSteps = getVisibleSteps();

  return (
    <motion.div
      className="w-full h-full rounded-lg overflow-y-scroll"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      ref={stepsContainerRef}
    >
      {/* User query section */}
      <div className="text-sm p-6 flex items-center space-x-4">
        <UserAvatar />
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <p className="text-white text-sm leading-tight">{cohortState.userQuery}</p>
        </motion.div>
      </div>

      {/* Steps sections */}
      <div className="space-y-4">
        {visibleSteps.map((stepType) => {
          const step = steps[stepType];
          const isActive = stepType === currentStep;
          const isCompleted = step.status === CohortStepStatus.Completed;
          const config = STEP_CONFIG[stepType];
          const hasError = step.status === CohortStepStatus.Error;

          const StepComponent = STEP_COMPONENTS[stepType] as React.FC<StepComponentProps>;

          if (!step || !step.data) {
            return null;
          }

          return (
            <div
              key={stepType}
              className={`${
                hasError ? 'bg-red-900/20 border-2 border-red-700' : 'bg-background-dark-subdued'
              } p-4 rounded-lg`}
            >
              <StepHeader title={STEP_CONFIG[stepType].title} isActive={isActive || isCompleted} />
              {step.status === CohortStepStatus.In_Progress && (
                <PulsingText text={config?.statusText || 'Processing...'} />
              )}
              {/* Render the appropriate step component based on step type */}
              <StepComponent
                step={{ ...step, type: stepType } as unknown as CodesSelectionStep}
                isActive={isActive}
                isCompleted={isCompleted}
                onAccept={handleAcceptClick}
                onEdit={handleEditClick}
                onCreateCohort={handleCreateCohortClick}
                isRunJobPending={isPending}
              />
            </div>
          );
        })}
      </div>

      {/* Empty div to scroll as the step content is written */}
      <div ref={bottomElementRef} />
    </motion.div>
  );
};
